import React from 'react';
import {
    Box,
    Chip,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    ListItemButton,
    Tooltip,
    tooltipClasses,
    styled
} from '@mui/material';
import {
    Event as EventIcon,
    Groups as GroupsIcon,
    ConnectWithoutContact as MatchingIcon,
    RocketLaunch as KickoffIcon
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';


interface MenuItemType {
    text: string;
    icon: React.ReactNode;
    path: string;
    tooltip: string;
}

interface MatchingPhaseSidebarProps {
    sidebarCollapsed?: boolean;
}

const MatchingPhaseSidebar: React.FC<MatchingPhaseSidebarProps> = ({
    sidebarCollapsed = false
}) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { pathname } = location;

    // Extract organization_id and campaign_id from the URL path
    const pathParts = pathname.split('/');
    const organization_id = pathParts[2]; // /organizations/{organization_id}/...
    const campaign_id = pathParts[4]; // /organizations/{organization_id}/campaigns/{campaign_id}/...

    // No need for redirection here as it's handled in CampaignOverview

    const menuItems: MenuItemType[] = [

        {
            text: 'Applications',
            icon: <GroupsIcon />,
            path: `/organizations/${organization_id}/campaigns/${campaign_id}/matching/application-dashboard`,
            tooltip: 'View current applications'
        },
        {
            text: 'Matching',
            icon: <MatchingIcon />,
            path: `/organizations/${organization_id}/campaigns/${campaign_id}/matching/matching`,
            tooltip: 'Manage matching process'
        },
        {
            text: 'Dates',
            icon: <EventIcon />,
            path: `/organizations/${organization_id}/campaigns/${campaign_id}/matching/dates`,
            tooltip: 'Update important campaign dates'
        },
        {
            text: 'Kickoff',
            icon: <KickoffIcon />,
            path: `/organizations/${organization_id}/campaigns/${campaign_id}/matching/kickoff`,
            tooltip: 'Start the campaign'
        },
    ];

    const handleNavigation = (path: string) => {
        navigate(path);
    };

    // Custom tooltip style that appears immediately
    const CustomTooltip = styled(Tooltip)(({ theme }) => ({
        [`& .${tooltipClasses.tooltip}`]: {
            marginLeft: '8px !important',
            marginRight: '8px !important',
            backgroundColor: 'rgba(97, 97, 97, 0.9)',
            color: '#fff',
            fontSize: '0.8rem',
            padding: '8px 12px',
            borderRadius: '4px',
        },
    }));

    return (
        <>
            {!sidebarCollapsed && (
                <Box sx={{ px: 1, mb: 1 }}>
                    <Chip
                        label="Matching Phase"
                        sx={{
                            width: '100%',
                            justifyContent: 'center',
                            fontSize: '1rem',
                            height: '36px',
                            backgroundColor: '#ed6c02',  // Orange color
                            color: 'white',
                            fontWeight: '500'
                        }}
                    />
                </Box>
            )}

            <List sx={{ px: sidebarCollapsed ? 0 : 1 }}>
                {menuItems.map((item) => (
                    <CustomTooltip
                        key={item.text}
                        title={item.tooltip}
                        placement="right"
                        arrow
                        disableFocusListener={!sidebarCollapsed}
                        disableHoverListener={!sidebarCollapsed}
                        enterDelay={0}
                    >
                        <ListItem disablePadding sx={{ display: 'block', mb: 0.5 }}>
                            <ListItemButton
                                sx={{
                                    minHeight: 48,
                                    justifyContent: sidebarCollapsed ? 'center' : 'initial',
                                    px: 2.5,
                                    borderRadius: 1,
                                    backgroundColor: pathname.includes(item.path) ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
                                    '&:hover': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                    },
                                }}
                                onClick={() => handleNavigation(item.path)}
                            >
                                <ListItemIcon
                                    sx={{
                                        minWidth: 0,
                                        mr: sidebarCollapsed ? 0 : 3,
                                        justifyContent: 'center',
                                        color: pathname.includes(item.path) ? 'primary.main' : 'inherit',
                                    }}
                                >
                                    {item.icon}
                                </ListItemIcon>
                                {!sidebarCollapsed && (
                                    <ListItemText
                                        primary={item.text}
                                        primaryTypographyProps={{
                                            fontWeight: pathname.includes(item.path) ? '500' : '400',
                                            color: 'text.primary',
                                        }}
                                    />
                                )}
                            </ListItemButton>
                        </ListItem>
                    </CustomTooltip>
                ))}
            </List>
        </>
    );
};

export default MatchingPhaseSidebar;